import React, { useEffect, useState, useMemo, useCallback } from "react";
import { AlertTriangle, Truck, Ban } from "lucide-react";
import api, { extractErrorMessage } from "../lib/api";
import Modal from "./Modal";
import AdminActionModal from "./AdminActionModal";
import { toast } from "sonner";

/**
 * LTL Freight Data Modal — Round-13 rewrite.
 *
 * Source of truth is now the `pallets` collection. Captures W/L/H/Weight per
 * individual PLL (both reserved + produced rows). Also exposes a per-row
 * Void action so operators can flag "printed too many tags" pallets and keep
 * them off the Packing List / Pallet Summary.
 *
 * Props:
 *   open    bool
 *   order   the order doc
 *   onClose fn   — close request
 *   onSaved fn   — called whenever a save / void happens, so the parent can
 *                  reload its data (gate banner, RP totals, etc.)
 */
export default function FreightDataModal({ open, order, onClose, onSaved }) {
    const [pallets, setPallets] = useState([]);
    const [draft, setDraft] = useState({});     // {pid: {width_in, length_in, ...}}
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [voidTarget, setVoidTarget] = useState(null);

    const reload = useCallback(async () => {
        if (!order?.id) return;
        try {
            const { data } = await api.get(`/orders/${order.id}/pallets`);
            setPallets(data);
            // Seed draft inputs from existing freight values.
            const next = {};
            data.forEach((p) => {
                if (p.status === "voided") return;
                next[p.id] = {
                    width_in: p.freight?.width_in ?? "",
                    length_in: p.freight?.length_in ?? "",
                    height_in: p.freight?.height_in ?? "",
                    weight_lbs: p.freight?.weight_lbs ?? "",
                };
            });
            setDraft(next);
        } catch (err) {
            console.error("Failed to load pallets", err);
        }
    }, [order?.id]);

    useEffect(() => {
        if (open) reload();
    }, [open, reload]);

    const active = useMemo(
        () => pallets.filter((p) => p.status !== "voided"),
        [pallets],
    );

    const cuFt = useCallback((r) => {
        const w = Number(r?.width_in) || 0;
        const l = Number(r?.length_in) || 0;
        const h = Number(r?.height_in) || 0;
        return w * l * h / 1728;
    }, []);

    const totals = useMemo(() => {
        let wt = 0;
        let cu = 0;
        active.forEach((p) => {
            const r = draft[p.id] || {};
            wt += Number(r.weight_lbs) || 0;
            cu += cuFt(r);
        });
        return { wt, cu, count: active.length };
    }, [active, draft, cuFt]);

    if (!open) return null;

    const setRow = (pid, patch) =>
        setDraft((d) => ({ ...d, [pid]: { ...(d[pid] || {}), ...patch } }));

    const allFilled = active.every((p) => {
        const r = draft[p.id] || {};
        return (
            Number(r.width_in) > 0 &&
            Number(r.length_in) > 0 &&
            Number(r.height_in) > 0 &&
            Number(r.weight_lbs) > 0
        );
    });

    const saveAll = async () => {
        setBusy(true);
        setError("");
        try {
            let saved = 0;
            for (const p of active) {
                const r = draft[p.id] || {};
                if (!(Number(r.width_in) > 0 && Number(r.length_in) > 0 &&
                      Number(r.height_in) > 0 && Number(r.weight_lbs) > 0)) {
                    continue;
                }
                await api.patch(`/pallets/${p.id}/freight`, {
                    width_in: Number(r.width_in),
                    length_in: Number(r.length_in),
                    height_in: Number(r.height_in),
                    weight_lbs: Number(r.weight_lbs),
                });
                saved += 1;
            }
            await reload();
            onSaved?.();
            // Round-23 — auto-close + toast on success. Previously the modal
            // stayed open after a successful save which left operators unsure
            // whether the data persisted.
            toast.success(
                `LTL freight saved for ${saved} pallet${saved === 1 ? "" : "s"}`,
            );
            onClose?.();
        } catch (err) {
            console.error("Freight save failed", err);
            setError(extractErrorMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Modal
                title={
                    <span className="inline-flex items-center gap-2">
                        <Truck className="w-5 h-5 text-red-700" />
                        LTL Freight Data · Order {order?.internal_number}
                    </span>
                }
                onClose={onClose}
                size="xl"
                testId="freight-modal"
            >
                <div className="space-y-3">
                    <div className="flex gap-3 items-start text-sm text-gray-700 border border-amber-200 bg-amber-50 rounded-sm px-3 py-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            Every pallet on this LTL order — reserved or produced —
                            needs <strong>Width × Length × Height</strong> (inches) and{" "}
                            <strong>Weight</strong> (lbs) before a Packing List can be
                            generated. If a pallet was printed but never produced, use
                            the <strong>Void</strong> button instead so it disappears
                            from shipment documents.
                        </div>
                    </div>

                    {active.length === 0 ? (
                        <div className="text-sm text-gray-500 italic p-6 text-center">
                            No active pallets yet. Generate an advance Load Tag or
                            record production to create them.
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-sm overflow-x-auto">
                            <table className="odr-table" data-testid="freight-table">
                                <thead>
                                    <tr>
                                        <th>Pallet ID</th>
                                        <th>Status</th>
                                        <th className="text-right">Units</th>
                                        <th className="text-right">Width</th>
                                        <th className="text-right">Length</th>
                                        <th className="text-right">Height</th>
                                        <th className="text-right">Weight</th>
                                        <th className="text-right">Cubic Ft</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {active.map((p) => {
                                        const r = draft[p.id] || {};
                                        const cu = cuFt(r);
                                        return (
                                            <tr
                                                key={p.id}
                                                data-testid={`freight-row-${p.pallet_number}`}
                                            >
                                                <td className="odr-mono text-xs font-semibold">
                                                    {p.pallet_number}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`odr-badge ${
                                                            p.status === "produced"
                                                                ? "odr-badge-completed"
                                                                : "odr-badge-open"
                                                        }`}
                                                    >
                                                        {p.status}
                                                    </span>
                                                </td>
                                                <td className="text-right odr-mono text-xs">
                                                    {Number(p.units_per_pallet).toLocaleString()}
                                                </td>
                                                {["width_in", "length_in", "height_in", "weight_lbs"].map(
                                                    (k) => (
                                                        <td key={k} className="text-right">
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={r[k] ?? ""}
                                                                onChange={(e) =>
                                                                    setRow(p.id, {
                                                                        [k]: e.target.value,
                                                                    })
                                                                }
                                                                className="odr-input w-20 rounded-sm border border-gray-300 px-2 text-sm text-right odr-mono"
                                                                data-testid={`freight-${p.pallet_number}-${k}`}
                                                            />
                                                        </td>
                                                    ),
                                                )}
                                                <td className="text-right odr-mono text-xs text-gray-600">
                                                    {cu > 0 ? cu.toFixed(4) : "—"}
                                                </td>
                                                <td className="text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => setVoidTarget(p)}
                                                        className="text-xs text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                                        data-testid={`void-${p.pallet_number}`}
                                                        title="Mark this pallet as unused (won't appear on PL/Summary)"
                                                    >
                                                        <Ban className="w-3.5 h-3.5" /> Void
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-50 font-semibold">
                                        <td colSpan={6} className="text-right">
                                            TOTAL ({totals.count} pallets)
                                        </td>
                                        <td className="text-right odr-mono">
                                            {totals.wt.toFixed(2)}
                                        </td>
                                        <td className="text-right odr-mono">
                                            {totals.cu.toFixed(4)}
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {error && (
                        <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <div className="text-xs text-gray-500">
                            Cubic Feet = W × L × H ÷ 1728 (auto-calculated)
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={busy}
                                className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                data-testid="freight-close"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={saveAll}
                                disabled={!allFilled || busy}
                                className="odr-btn rounded-sm bg-red-700 hover:bg-red-800 text-white text-sm font-semibold px-4 disabled:opacity-50"
                                data-testid="freight-submit"
                                title={
                                    !allFilled
                                        ? "Fill (or void) every row first"
                                        : "Save freight for every pallet"
                                }
                            >
                                {busy ? "Saving…" : "Save Freight Data"}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            <AdminActionModal
                open={!!voidTarget}
                title={voidTarget ? `Void pallet ${voidTarget.pallet_number}` : ""}
                description={
                    voidTarget ? (
                        <span>
                            This pallet will be marked <strong>voided/unused</strong>{" "}
                            and excluded from the Packing List, Pallet Summary, and
                            shipment documents.
                            {voidTarget.freight ? (
                                <em className="block mt-1 text-xs">
                                    Note: this pallet already has freight data captured —
                                    the audit log will record that.
                                </em>
                            ) : null}
                        </span>
                    ) : null
                }
                confirmLabel="Void Pallet"
                minReasonLength={5}
                onCancel={() => setVoidTarget(null)}
                onConfirm={async (reason) => {
                    await api.post(`/pallets/${voidTarget.id}/void`, { reason });
                    setVoidTarget(null);
                    await reload();
                    onSaved?.();
                }}
                testIdPrefix="void-pallet"
            />
        </>
    );
}
